import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Checkbox } from '../../ui/checkbox';
import { Button } from '../../ui/Button';

const SendingPreferenceSection = ({
  formData,
  handleInputChange,
  sendingPreferenceOptions,
  showSendingPreferenceModal,
  setShowSendingPreferenceModal,
  pendingSendingPreference,
  setPendingSendingPreference,
  isDark,
  isEmbedded = false
}) => {
  const [showPostalTooltip, setShowPostalTooltip] = useState(false);

  useEffect(() => {
    if (showSendingPreferenceModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSendingPreferenceModal]);

  const modalContent = showSendingPreferenceModal ? (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={() => setShowSendingPreferenceModal(false)}
    >
      <div
        className={`rounded-[12px] p-[32px] max-w-[420px] w-full mx-4 shadow-lg flex flex-col items-center gap-[24px] transition-colors duration-200 ${
          isDark ? 'bg-[#161515]' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <p className={`text-[16px] font-semibold text-center m-0 transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          Are you sure you want to change the sending preference?
        </p>

        <div className="flex gap-[16px] w-full">
          <Button
            variant="ghost"
            className={`flex-1 text-[16px] px-[16px] py-[12px] rounded-[12px] transition-colors duration-200 ${
              isDark
                ? 'bg-[#cd6d2c] text-[#000000] hover:bg-[#e37f3d]'
                : 'bg-[#cd6d2c] text-white hover:bg-[#b35d24]'
            }`}
            onClick={() => {
              handleInputChange('sending_preference', pendingSendingPreference);
              if (pendingSendingPreference !== 'postal') {
                handleInputChange('delivery_preference', '');
              }
              setShowSendingPreferenceModal(false);
              setPendingSendingPreference(null);
            }}
          >
            Confirm
          </Button>
          <Button
            variant="ghost"
            className={`flex-1 text-[16px] px-[16px] py-[12px] rounded-[12px] transition-colors duration-200 ${
              isDark
                ? 'bg-[#000000] text-[#ffffff] hover:bg-[#1a1a1a]'
                : 'bg-[#D9D9D9] text-[#072837] hover:bg-[#D9D9D9]'
            }`}
            onClick={() => {
              setShowSendingPreferenceModal(false);
              setPendingSendingPreference(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  ) : null;

  // Check if postal checkbox should be disabled (when postal_only is selected)
  const isPostalDisabled = (optionValue) => {
    return optionValue === 'postal' &&
           formData.sending_preference === 'postal' &&
           formData.delivery_preference === 'postal_only';
  };

  const content = (
    <>
      {/* Options */}
      <div className="flex flex-col gap-[16px]">
        {sendingPreferenceOptions.map((option) => {
          const isDisabled = isPostalDisabled(option.value);

          return (
            <div key={option.value} className="flex flex-col gap-[16px]">
              <div
                className="relative flex items-center gap-[8px]"
                onMouseEnter={() => isDisabled && setShowPostalTooltip(true)}
                onMouseLeave={() => setShowPostalTooltip(false)}
              >
                <Checkbox
                  id={`sending-preference-${option.value.replace(/\s+/g, '-').toLowerCase()}`}
                  checked={formData.sending_preference === option.value}
                  onCheckedChange={(checked) => {
                    if (checked && formData.sending_preference !== option.value) {
                      setPendingSendingPreference(option.value);
                      setShowSendingPreferenceModal(true);
                    } else if (!checked) {
                      // Prevent unchecking postal if postal_only is selected
                      if (isDisabled) return;

                      handleInputChange('sending_preference', '');
                      // Clear delivery_preference when unchecking postal
                      if (option.value === 'postal') {
                        handleInputChange('delivery_preference', '');
                      }
                    }
                  }}
                  isDark={isDark}
                />
                <label
                  htmlFor={`sending-preference-${option.value.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`text-[16px] font-normal cursor-pointer transition-colors duration-200 ${
                    isDark ? 'text-white' : 'text-[#072837]'
                  }`}
                >
                  {option.name}
                </label>

                {/* Tooltip when postal is disabled */}
                {isDisabled && showPostalTooltip && (
                  <div className={`absolute left-0 top-full mt-[4px] px-[12px] py-[8px] rounded-[8px] text-[12px] font-normal whitespace-nowrap transition-all duration-200 z-[100] shadow-lg ${
                    isDark ? 'bg-[#292929] text-white' : 'bg-[#333333] text-white'
                  }`}>
                    Please uncheck "Send to recipients who elected post only" first
                    <div className={`absolute left-[20px] bottom-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent ${
                      isDark ? 'border-b-[#292929]' : 'border-b-[#333333]'
                    }`}></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Helper text */}
      <div className={`text-[14px] font-normal transition-colors duration-200 ${
        isDark ? 'text-[#b3b3b3]' : 'text-[#686868]'
      }`}>
        This should only ever be used in special circumstances e.g. urgent correspondence to be delivered or under committee instruction. If you are overriding to send to all via email - consider using Front Instead
      </div>
    </>
  );

  if (isEmbedded) {
    return (
      <>
        {modalContent && createPortal(modalContent, document.body)}
        {content}
      </>
    );
  }

  return (
    <>
      {modalContent && createPortal(modalContent, document.body)}

      {/* Sending Preference Section */}
      <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col gap-[16px] transition-colors duration-200 ${
        isDark ? 'bg-[#000000]' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`text-[16px] font-semibold transition-colors duration-200 ${
          isDark ? 'text-white' : 'text-[#072837]'
        }`}>
          Communication Preference Override
        </div>

        {content}
      </div>
    </>
  );
};

export default SendingPreferenceSection;
